import {
    useBundleForWaste,
    useBundleForWasteAsof,
    useWasteById,
    useWasteByIdAsof,
    Waste,
    WasteFlavorStub
} from "../rpc/webrpc-client.gen.ts";
import {WasteEditor} from "./editor.tsx";
import {EditorMode} from "../forms.tsx";
import {Stack} from "@mui/joy";
import {RevisionBrowser} from "../components/RevisionBrowser.tsx";
import {spawnTstzSpan} from "../tstzspan.ts";
import {DateArg, formatISO} from "date-fns";
import {useShowableWasteFlavors} from "../wasteFlavor/wasteFlavor.tsx";
import {
    allWasteRevisionStubsTqxo,
    useAllWasteRevisionStubs,
    useLiveLastValidWasteById,
    useMothership
} from "../tq/tqx.tsx";
import {SuspenseDialog} from "../components/SuspenseDialog.tsx";
import {useRefreshableQuery} from "../util/tqrefresh.tsx";
import {NADA} from "../util/arutil.tsx";

export interface ActiveWasteCargoProps {
    wasteId: string;
    active: true;
    mode?: EditorMode;
    onFlavorChange?: (newFlavor: WasteFlavorStub) => void;
    onAsofChange?: (newAsof: DateArg<Date> | undefined) => void;
    afterSpawn?: ({waste}: { waste: Waste }) => void;
    afterPatch?: () => void;
}

export function ActiveWasteCargoView(
    {
        wasteId,
        mode: inMode,
        onFlavorChange,
        onAsofChange,
        afterSpawn,
        afterPatch,
    }: ActiveWasteCargoProps) {
    const mama = useMothership();
    const wasteTqrv = useWasteById({wasteId});
    const bundleTqrv = useBundleForWaste({wasteId});

    const {data: revisionStubs} = useRefreshableQuery(allWasteRevisionStubsTqxo(mama, {wasteId}));
    const {data: flavors}
        = useShowableWasteFlavors();

    if (wasteTqrv.isPending || bundleTqrv.isPending) {
        return null;
    }

    const bundle = bundleTqrv.data;
    const waste = wasteTqrv.data;
    const mode = inMode;
    const revs = revisionStubs ?? NADA;

    if (!bundle) {
        throw new Error(`no bundle for waste ${wasteId}`);
    }
    return <Stack direction="column">
        {(mode !== "rw" && revs.length > 1)
            ? <RevisionBrowser revisions={revs}
                               onSelectionChange={(selectedRevision) => {
                                   if (onAsofChange) {
                                       const insideRev = selectedRevision.validSpan.guaranteedIncluded();
                                       if (insideRev) {
                                           onAsofChange(insideRev);
                                       }
                                   }
                               }}/>
            : null}
        <WasteEditor waste={waste} bundleId={bundle.id} mode={mode}
                     possibleFlavors={flavors} onFlavorChange={onFlavorChange}
                     afterSpawn={afterSpawn} afterPatch={afterPatch}/>
    </Stack>
}

export interface WasteCargoHistoryProps {
    wasteId: string;
    active: false;
    asof?: DateArg<Date>;
    onAsofChange?: (newAsof: DateArg<Date> | undefined) => void;
}

export function WasteCargoHistoryView(
    {
        wasteId,
        asof: inAsof,
        onAsofChange,
    }: WasteCargoHistoryProps) {
    const asof = inAsof ?? new Date();
    const isoAsof = formatISO(asof);
    const wasteTqrv = useWasteByIdAsof({wasteId, asof: isoAsof});
    const revisionStubsTqrv
        = useAllWasteRevisionStubs({wasteId: wasteId});
    const bundleTqrv
        = useBundleForWasteAsof({wasteId: wasteId, asof: isoAsof});

    if (!wasteTqrv.isSuccess || !revisionStubsTqrv.isSuccess || !bundleTqrv.isSuccess) {
        return null;
    }

    const bundle = bundleTqrv.data;
    const waste = wasteTqrv.data;
    const revisionStubs = revisionStubsTqrv.data;
    if (!bundle) {
        throw new Error(`no bundle for waste ${wasteId}`);
    }
    return <Stack direction="column">
        <RevisionBrowser revisions={revisionStubs}
                         asof={asof}
                         onSelectionChange={(selectedRevision) => {
                             if (onAsofChange) {
                                 const insideRev = selectedRevision.validSpan.guaranteedIncluded();
                                 onAsofChange(insideRev);
                             }
                         }}/>
        <WasteEditor waste={waste} bundleId={bundle.id} mode="r"/>
    </Stack>
}

export type WasteCargoProps = ActiveWasteCargoProps | WasteCargoHistoryProps;

export function WasteCargoView(props: WasteCargoProps) {
    const {wasteId, active, ...rest} = props;

    const wasteTqrv = useLiveLastValidWasteById({wasteId});
    if (!wasteTqrv.isSuccess) {
        return <SuspenseDialog message="fetching waste"/>;
    }
    const activeValidSpan = spawnTstzSpan(wasteTqrv.data.validSpan);
    const isActive = active || activeValidSpan.contains(props.asof);

    if (isActive) {
        return <ActiveWasteCargoView wasteId={wasteId} active={true} {...rest}/>;
    } else {
        return <WasteCargoHistoryView wasteId={wasteId} active={false} {...rest}/>
    }
}

